import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import { ConsultantSeniorityValues, type ConsultantSeniority, type UpdateProfileDto } from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';

export function SupplierSeniorityCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const [yearsDraft, setYearsDraft] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  useEffect(() => {
    if (profile?.yearsOfExperience != null) {
      setYearsDraft(String(profile.yearsOfExperience));
    } else {
      setYearsDraft('');
    }
  }, [profile?.yearsOfExperience]);

  const mutation = useMutation({
    mutationFn: (dto: UpdateProfileDto) => profileApi.updateProfile(dto),
    onSuccess: (data) => {
      onProfileMutationSuccess(queryClient, data);
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const seniority = profile?.consultantSeniority ?? '';

  const onSeniorityChange = (v: string) => {
    const next = v === '__clear__' ? null : (v as ConsultantSeniority);
    mutation.mutate({ consultantSeniority: next });
  };

  const commitYears = () => {
    const trimmed = yearsDraft.trim();
    if (trimmed === '') {
      mutation.mutate({ yearsOfExperience: null });
      return;
    }
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n) || n < 0 || n > 60) {
      return;
    }
    mutation.mutate({ yearsOfExperience: n });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.seniority.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.seniority.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="years-of-experience">{t('profile.seniority.yearsLabel')}</Label>
              <Input
                id="years-of-experience"
                type="number"
                min={0}
                max={60}
                value={yearsDraft}
                onChange={(e) => setYearsDraft(e.target.value)}
                onBlur={commitYears}
                className="max-w-xs"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('profile.seniority.levelLabel')}</Label>
              <Select
                value={seniority || '__clear__'}
                onValueChange={onSeniorityChange}
                disabled={mutation.isPending}
              >
                <SelectTrigger className="max-w-xs">
                  <SelectValue placeholder={t('profile.seniority.levelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__clear__">{t('profile.seniority.levelClear')}</SelectItem>
                  {ConsultantSeniorityValues.map((level) => (
                    <SelectItem key={level} value={level}>
                      {t(`profile.seniority.levels.${level}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
